// Shared brand primitives for the Money Back Australia app — the RN counterpart
// of the marketing brand system (Brand System.html). Coin monogram, lockup,
// app bar + progress, buttons, chips, eyebrows and cards. Screens compose these
// so the refresh stays a one-file change.
import { type ReactNode } from "react";
import {
  Linking,
  Platform,
  Pressable,
  type PressableProps,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from "react-native";

import { MARKETING_URL } from "../env";
import { colors, fonts, radius, space } from "../theme";

/** Send the user back to the marketing site (the brand "home" / calculator
 * hub). Same-tab navigation on web; opens the browser on native. */
function openMarketingHome(): void {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.location.href = MARKETING_URL;
    return;
  }
  void Linking.openURL(MARKETING_URL);
}

/** The "mb" coin. `size` drives the diameter; the glyph is ~0.5× and nudged
 * up 3% (matching the CSS `.mono`). `variant` flips the fill for dark bands. */
export function Coin({
  size = 32,
  variant = "honey",
}: {
  size?: number;
  variant?: "honey" | "ink";
}): JSX.Element {
  const onInk = variant === "ink";
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: onInk ? colors.ink : colors.honey,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Text
        style={{
          fontFamily: fonts.displayHeavy,
          fontSize: size * 0.5,
          lineHeight: size * 0.5,
          letterSpacing: -size * 0.025,
          color: onInk ? colors.honey : colors.cream,
          marginTop: -size * 0.03,
        }}
      >
        mb
      </Text>
    </View>
  );
}

/** Coin + "Money Back Australia" wordmark. `onDark` flips wordmark colours. */
export function Lockup({
  size = 30,
  onDark = false,
}: {
  size?: number;
  onDark?: boolean;
}): JSX.Element {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: size * 0.32 }}>
      <Coin size={size} variant={onDark ? "ink" : "honey"} />
      <Text
        style={{
          fontFamily: fonts.displayHeavy,
          fontSize: size * 0.6,
          letterSpacing: -0.5,
          color: onDark ? colors.cream : colors.ink,
        }}
      >
        Money Back{" "}
        <Text style={{ color: onDark ? colors.honey : colors.honeyDeep }}>
          Australia
        </Text>
      </Text>
    </View>
  );
}

/** Mono uppercase eyebrow (eyebrows above section/headline titles). */
export function Eyebrow({
  children,
  onDark = false,
}: {
  children: ReactNode;
  onDark?: boolean;
}): JSX.Element {
  return (
    <Text
      style={[styles.eyebrow, onDark && { color: colors.honey }]}
      accessibilityRole="header"
    >
      {children}
    </Text>
  );
}

export type ChipTone = "owed" | "fy" | "est" | "ocean";

/** Small rounded status chip. */
export function Chip({
  label,
  tone = "fy",
}: {
  label: string;
  tone?: ChipTone;
}): JSX.Element {
  const tones: Record<ChipTone, { bg: string; fg: string }> = {
    owed: { bg: colors.ocean, fg: "#FFFFFF" },
    ocean: { bg: colors.oceanTint, fg: colors.oceanDeep },
    fy: { bg: colors.sand, fg: colors.inkSoft },
    est: { bg: colors.honeyTint, fg: colors.honeyDeep },
  };
  const c = tones[tone];
  return (
    <View style={[styles.chip, { backgroundColor: c.bg }]}>
      <Text style={[styles.chipText, { color: c.fg }]}>{label}</Text>
    </View>
  );
}

export type ButtonVariant = "primary" | "ink" | "outline" | "ghost";

/** Brand button. Primary = honey bg / ink text (the rule of the system). */
export function Button({
  label,
  onPress,
  variant = "primary",
  full = false,
  disabled = false,
  testID,
}: {
  label: string;
  onPress: () => void;
  variant?: ButtonVariant;
  full?: boolean;
  disabled?: boolean;
  testID?: string;
}): JSX.Element {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      testID={testID}
      accessibilityRole="button"
      style={({ pressed }) => [
        styles.btn,
        variant === "primary" && styles.btnPrimary,
        variant === "ink" && styles.btnInk,
        variant === "outline" && styles.btnOutline,
        variant === "ghost" && styles.btnGhost,
        full && { alignSelf: "stretch" },
        pressed && { transform: [{ translateY: 1 }] },
        disabled && { opacity: 0.4 },
      ]}
    >
      <Text
        style={[
          styles.btnText,
          variant === "primary" && { color: colors.ink },
          variant === "ink" && { color: colors.cream },
          variant === "outline" && { color: colors.ink },
          variant === "ghost" && { color: colors.honeyDeep },
        ]}
      >
        {label}
      </Text>
    </Pressable>
  );
}

/** A white surface card with the brand shadow/hairline. */
export function Card({
  children,
  style,
  tone = "card",
}: {
  children: ReactNode;
  style?: ViewStyle;
  tone?: "card" | "sand" | "ink";
}): JSX.Element {
  return (
    <View
      style={[
        styles.card,
        tone === "sand" && { backgroundColor: colors.sand, borderColor: colors.line },
        tone === "ink" && { backgroundColor: colors.ink, borderColor: colors.ink },
        style,
      ]}
    >
      {children}
    </View>
  );
}

/** Thin honey progress bar (calculator app bar). `value` is 0..1. */
export function ProgressBar({ value }: { value: number }): JSX.Element {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <View style={styles.progressTrack}>
      <View style={[styles.progressFill, { width: `${pct}%` }]} />
    </View>
  );
}

/** Calculator app bar: lockup + a product pill, optional "start over" and an
 * underlying progress bar. */
export function AppBar({
  pill,
  onRestart,
  progress,
}: {
  pill?: string;
  onRestart?: () => void;
  progress?: number;
}): JSX.Element {
  return (
    <View style={styles.appBarWrap}>
      <View style={styles.appBar}>
        <Pressable
          onPress={openMarketingHome}
          accessibilityRole="link"
          accessibilityLabel="Money Back Australia home"
          testID="appbar-home"
        >
          <Lockup size={28} />
        </Pressable>
        <View style={styles.appBarRight}>
          {pill !== undefined ? (
            <View style={styles.appBarPill}>
              <Text style={styles.appBarPillText}>{pill}</Text>
            </View>
          ) : null}
          {onRestart !== undefined ? (
            <Pressable onPress={onRestart} accessibilityRole="button" testID="appbar-restart">
              <Text style={styles.restart}>↺ Start over</Text>
            </Pressable>
          ) : null}
        </View>
      </View>
      {progress !== undefined ? <ProgressBar value={progress} /> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  eyebrow: {
    fontFamily: fonts.mono,
    fontSize: 12,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: colors.honeyDeep,
  } as TextStyle,

  chip: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: radius.pill,
    paddingVertical: 6,
    paddingHorizontal: 13,
  },
  chipText: { fontFamily: fonts.bodySemibold, fontSize: 12.5 },

  btn: {
    borderRadius: radius.md,
    paddingVertical: 15,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  btnPrimary: { backgroundColor: colors.honey },
  btnInk: { backgroundColor: colors.ink },
  btnOutline: {
    backgroundColor: "transparent",
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
  },
  btnGhost: { backgroundColor: "transparent", paddingHorizontal: 6 },
  btnText: { fontFamily: fonts.bodyBold, fontSize: 16 },

  card: {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.xl,
    padding: space.xl,
    shadowColor: "#221C15",
    shadowOpacity: 0.08,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 14 },
  },

  progressTrack: {
    height: 3,
    backgroundColor: colors.line,
    width: "100%",
  },
  // honey-deep (not honey) so the fill clears 3:1 against the faint track.
  progressFill: { height: 3, backgroundColor: colors.honeyDeep },

  appBarWrap: {
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
    backgroundColor: colors.cream,
  },
  appBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: space.lg,
    height: 60,
  },
  appBarRight: { flexDirection: "row", alignItems: "center", gap: space.md },
  appBarPill: {
    backgroundColor: colors.honeyTint,
    borderRadius: radius.pill,
    paddingVertical: 5,
    paddingHorizontal: 12,
  },
  appBarPillText: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.honeyDeep,
  },
  restart: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.inkSoft },
});
